import { computed } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { PAYMENT_PROVIDER_STATUS } from '@/graphql/Queries/payment';
import type { PaymentProviderFieldStatus, PaymentProviderStatus } from '@/types/composition-functions';

// Config-state of a payment provider, derived server-side from the provider
// registry (#238). Carries field NAMES and presence flags only — the backend
// never returns the stored config values. Types come from GraphQL codegen.
export type { PaymentProviderFieldStatus, PaymentProviderStatus };

/**
 * Human-readable setup summary for one provider, e.g.
 * "Properly configured" or "Needs setup — missing Service Type ID, API Key".
 * Pure so it can be unit-tested without Apollo.
 */
export function summarizeProviderStatus(status: PaymentProviderStatus): string {
  if (status.isConfigured) {
    return 'Properly configured';
  }

  const missingLabels = status.missingFields.map((key) => {
    const field = status.fields.find((f) => f.key === key);
    return field?.label ?? key;
  });

  if (!missingLabels.length) {
    return 'Not set up';
  }

  return `Needs setup — missing ${missingLabels.join(', ')}`;
}

export function usePaymentProviderStatus() {
  const { result, loading, refetch } = useQuery<{ paymentProviderStatus: PaymentProviderStatus[] }>(
    PAYMENT_PROVIDER_STATUS,
    {},
    { fetchPolicy: 'cache-and-network' },
  );

  const providerStatuses = computed<PaymentProviderStatus[]>(
    () => result.value?.paymentProviderStatus ?? [],
  );

  return {
    providerStatuses,
    loadingProviderStatus: loading,
    refetchProviderStatus: refetch,
  };
}
